import {
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
} from "downshift";
import { useDownshiftEnvironment } from "../../../../internal/hooks";
import {
  SelectFieldGroupByValue,
  SelectFieldOption,
  SelectFieldPinnedOptions,
} from "../types";
import { SelectItem } from "../../../../internal/types/selectFieldInternalTypes";
import { useMemo } from "react";
import { useProcessedOptions } from "../../../../internal/hooks/useProcessedOptions";
import {
  buildSelectItems,
  toSelectItem,
} from "../../../../internal/functions/buildSelectItems";

export type UseSelectModeOptions = {
  options: SelectFieldOption[];
  pinned?: SelectFieldPinnedOptions;
  groupToString?: (groupValue: SelectFieldGroupByValue) => string;
  groupSorter?: (
    a: SelectFieldGroupByValue,
    b: SelectFieldGroupByValue,
  ) => number;
  selectedOption: SelectFieldOption | null;
  onSelectedOptionChange: (option: SelectFieldOption | null) => void;
  onIsOpenChange?: (changes: { isOpen?: boolean }) => void;
  displayAs: "popover" | "dialog";
  disableHighlightOnOpen?: boolean;
  disabled: boolean;
  virtualize?: boolean;
  onScrollToHighlight?: (index: number) => void;
};

/**
 * Wraps downshift's useSelect for non-searchable select fields.
 * Returns a compatible interface to useCombo (minus getInputProps/inputValue).
 */
export const useSelectMode = ({
  options,
  pinned,
  groupToString,
  groupSorter,
  selectedOption,
  onSelectedOptionChange,
  onIsOpenChange,
  displayAs = "popover",
  disableHighlightOnOpen = false,
  disabled,
  virtualize = false,
  onScrollToHighlight,
}: UseSelectModeOptions) => {
  const { pinnedSections, ungroupedItems, groupSections } = useProcessedOptions(
    {
      options,
      pinned,
      searchValue: "",
      groupToString,
      groupSorter,
    },
  );

  const { items, sectionsMeta } = useMemo(
    () =>
      buildSelectItems({
        pinnedSections,
        groupSections,
        ungroupedItems,
      }),
    [pinnedSections, groupSections, ungroupedItems],
  );

  const selectedDownshiftItem = useMemo(() => {
    return selectedOption ? toSelectItem(selectedOption) : null;
  }, [selectedOption]);

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    closeMenu,
    openMenu,
    setHighlightedIndex,
  } = useSelect({
    isItemDisabled: (item) => {
      if (!item) return false;
      return (disabled || item.disabled) ?? false;
    },
    environment,
    selectedItem: selectedDownshiftItem,
    onSelectedItemChange: ({
      selectedItem: si,
    }: {
      selectedItem: SelectItem;
    }) => {
      if (
        si &&
        (si.type === "option" ||
          si.type === "pinned-option" ||
          si.type === "grouped-option")
      ) {
        onSelectedOptionChange(si.original);
      } else {
        onSelectedOptionChange(null);
      }
    },
    onHighlightedIndexChange: ({ type, highlightedIndex: idx }) => {
      if (type !== useSelect.stateChangeTypes.ItemMouseMove && idx >= 0) {
        onScrollToHighlight?.(idx);
      }
    },
    onIsOpenChange: (changes) => {
      onIsOpenChange?.(changes);
      if (changes.isOpen && !disableHighlightOnOpen) {
        const selectedItemIndex = items.findIndex(
          (item) => item.original.id === selectedOption?.id,
        );
        setHighlightedIndex(selectedItemIndex);
      }
    },
    items: items,
    itemToString(item: SelectItem | null) {
      return item?.original.label ?? "";
    },
    stateReducer: (state, actionAndChanges) =>
      stateReducer(state, actionAndChanges, displayAs),
    ...(virtualize && { scrollIntoView: () => {} }),
  });

  return {
    wrapperDivRef,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    isOpen,
    items,
    sectionsMeta,
    closeMenu,
    openMenu,
  };
};

function stateReducer(
  state: UseSelectState<SelectItem>,
  actionAndChanges: UseSelectStateChangeOptions<SelectItem>,
  displayAs: "popover" | "dialog",
) {
  const { type, changes } = actionAndChanges;
  switch (type) {
    case useSelect.stateChangeTypes.ToggleButtonBlur:
      if (displayAs === "dialog" && state.isOpen) {
        return { ...changes, isOpen: state.isOpen };
      }
      return changes;
    case useSelect.stateChangeTypes.ToggleButtonKeyDownEscape:
    case useSelect.stateChangeTypes.FunctionCloseMenu:
      return changes;
    case useSelect.stateChangeTypes.ToggleButtonClick:
      if (displayAs === "dialog" && state.isOpen) {
        return { ...changes, isOpen: state.isOpen };
      }
      return changes;
    case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
    case useSelect.stateChangeTypes.ItemClick:
      return changes;
    default:
      return changes;
  }
}
